import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {NoAccessState} from '@/components/no-access-state'
import {ExpedienteChrome} from '@/components/expediente-chrome'
import {ExpedienteNotFound, ExpedienteEmptySnapshot} from '@/components/expediente-empty'
import {useExpedienteEntry, useExpedienteMatrizLegal} from '@/hooks/sgsst/expediente/use-expediente'
import {normLabel} from '@/lib/norma-label'

// La forma congelada de un ítem del snapshot legal (backend `SnapshotItem`).
type SnapshotItem = {
  norm_ref: string
  tipo: string
  numero: string
  anio: number
  titulo: string
  articulos_clave: string
  estado: string
  justificacion: string | null
}

const ESTADO_TONE: Record<string, 'green' | 'red' | 'neutral' | 'amber'> = {
  cumple: 'green',
  no_cumple: 'red',
  no_aplica: 'neutral',
  pendiente_verificar: 'amber',
}

/** El registro congelado del snapshot del cierre, nunca el vivo; solo lectura (UX O3). */
export function ExpedienteMatrizLegalPage() {
  const {t} = useTranslation()
  const {businessId, year: yearParam} = useParams()
  const year = Number(yearParam)
  const {canOperate, isLoading, exp} = useExpedienteEntry(businessId, year)
  const {data} = useExpedienteMatrizLegal(businessId, year, canOperate && !!exp)

  if (!canOperate) return <NoAccessState />
  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!exp) return <ExpedienteNotFound businessId={businessId!} year={year} />

  const base = `/businesses/${businessId}`
  const items = (data?.items ?? []) as unknown as SnapshotItem[]

  return (
    <ExpedienteChrome exp={exp} backTo={`${base}/expediente/${year}`} backLabel={t('expediente.backToIndex', {year})}>
      <div className="space-y-4" data-testid="expediente-matriz-legal">
        {!data?.disponible ? (
          <>
            <h2 className="font-mono text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('expediente.sections.matrizLegal')}
            </h2>
            <ExpedienteEmptySnapshot message={t('expediente.snapshotAusente.legal')} />
          </>
        ) : (
          <section className="surface overflow-hidden">
            <div className="surface-divider border-b px-4 py-3">
              <h2 className="font-heading text-base text-foreground">{t('expediente.sections.matrizLegal')}</h2>
            </div>
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                {t('expediente.vistas.matrizLegal.sinItems')}
              </p>
            ) : (
              items.map((item) => (
                <div
                  key={item.norm_ref}
                  data-testid="expediente-legal-item"
                  className="border-b border-border/60 px-4 py-3 last:border-b-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-medium tracking-wide text-primary uppercase">
                      {normLabel(item)}
                    </span>
                    <Tag tone={ESTADO_TONE[item.estado] ?? 'neutral'}>
                      {t(`expediente.vistas.matrizLegal.estado.${item.estado}`)}
                    </Tag>
                  </div>
                  <p className="mt-1 text-sm text-foreground">{item.titulo}</p>
                  {item.articulos_clave && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t('expediente.vistas.matrizLegal.articulos')}: {item.articulos_clave}
                    </p>
                  )}
                  {item.justificacion && (
                    <p className="mt-1 text-xs text-muted-foreground italic">{item.justificacion}</p>
                  )}
                </div>
              ))
            )}
          </section>
        )}
      </div>
    </ExpedienteChrome>
  )
}
